import React, { useEffect, useState, createContext, useContext } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/toaster';
import FloatingNav from './components/FloatingNav';
import HomePage from './pages/HomePage';
import PeptidesShopPage from './pages/PawnShopPage';
import ProductDetailPage from './pages/ProductDetailPage';
import LegacyProductRedirectPage from './pages/LegacyProductRedirectPage';
import LegacyProductSlugRedirectPage from './pages/LegacyProductSlugRedirectPage';
import ContactPage from './pages/ContactPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ResearchLibraryPage from './pages/research/ResearchLibraryPage';
import ResearchArticlePage from './pages/research/ResearchArticlePage';
import FAQPage from './pages/FAQPage';
import AboutUsPage from './pages/AboutUsPage';
import AdminLayout from './pages/AdminLayout';
import AdminSettingsLayout from './pages/admin/AdminSettingsLayout';
import DevSettingsLayout from './pages/dev/DevSettingsLayout';
import MaintenancePage from './pages/MaintenancePage';

// Legal Pages
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsConditionsPage from './pages/legal/TermsConditionsPage';
import AccessibilityPage from './pages/legal/AccessibilityPage';
import CompliancePage from './pages/legal/CompliancePage';
import ShippingReturnsPage from './pages/legal/ShippingReturnsPage';

// Location Pages (SEO)
import LocationPage from './pages/LocationPage';

// User Portal
import UserPortal from './pages/UserPortal';

// Age Verification
import AgeVerificationModal from './components/AgeVerificationModal';

// Chat Widget (Public Pages Only)
import ChatWidget from './components/ChatWidget';

import ChatPopout from './pages/admin/ChatPopout';

import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Site Settings Context
const SiteSettingsContext = createContext({ maintenanceMode: false, debugMode: false, loading: true });
export const useSiteSettings = () => useContext(SiteSettingsContext);

// Site settings provider component
const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({ maintenanceMode: false, debugMode: false, loading: true });

  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/settings/site`);
        const { site_name, favicon_url, maintenance_mode, debug_mode } = response.data;
        
        // Update document title
        if (site_name) {
          document.title = site_name;
        }
        
        // Update favicon dynamically
        if (favicon_url) {
          const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
          link.type = 'image/x-icon';
          link.rel = 'shortcut icon';
          link.href = favicon_url;
          document.getElementsByTagName('head')[0].appendChild(link);
        }
        
        // Enable debug mode logging
        if (debug_mode) {
          console.log('[DEBUG MODE] Site settings loaded:', response.data);
          window.__DEBUG_MODE__ = true;
        } else {
          window.__DEBUG_MODE__ = false;
        }
        
        setSettings({ 
          maintenanceMode: maintenance_mode || false, 
          debugMode: debug_mode || false, 
          loading: false 
        });
      } catch (error) {
        console.error('Error fetching site settings:', error);
        setSettings({ maintenanceMode: false, debugMode: false, loading: false });
      }
    };
    
    fetchSiteSettings();
  }, []);

  return (
    <SiteSettingsContext.Provider value={settings}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Layout wrapper for public pages
const PublicLayout = ({ children }) => {
  const location = useLocation();
  const hideNav = location.pathname === '/login' || 
    location.pathname === '/register' ||
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/dev');

  return (
    <>
      {!hideNav && <FloatingNav />}
      <main className="min-h-screen bg-slate-50">{children}</main>
      <ChatWidget />
    </>
  );
};

// Maintenance Mode Guard - wraps public routes
const MaintenanceGuard = ({ children }) => {
  const { maintenanceMode, loading } = useSiteSettings();
  const { isAuthenticated, user } = useAuth();
  
  // Show nothing while loading settings
  if (loading) {
    return (
      <div className="min-h-screen bg-void-base flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  // Check if user is admin (can bypass maintenance)
  const isAdmin = isAuthenticated && (user?.role === 'admin' || user?.role === 'super_admin');
  
  // Show maintenance page for non-admins when maintenance mode is enabled
  if (maintenanceMode && !isAdmin) {
    return <MaintenancePage />;
  }
  
  return children;
};

// Inner app component that uses auth context
const AppContent = () => {
  return (
    <div className="App bg-void-base min-h-screen">
      <BrowserRouter>
        <ScrollToTop />
        <AgeVerificationModal />
        <Routes>
          {/* Dev Settings Routes (Super Admin Only) */}
          <Route path="/dev/settings/*" element={<DevSettingsLayout />} />
          
          {/* Admin Settings Routes */}
          <Route path="/admin/settings/*" element={<AdminSettingsLayout />} />
          
          {/* Chat Popout - Standalone window (no sidebar) */}
          <Route path="/admin/chat/:chatId" element={<ChatPopout />} />
          
          {/* Admin Routes */}
          <Route path="/admin/*" element={<AdminLayout />} />
          
          {/* Location Pages - Standalone (no PublicLayout wrapper) - also protected by maintenance */}
          <Route path="/locations/:slug" element={
            <MaintenanceGuard>
              <LocationPage />
            </MaintenanceGuard>
          } />
          
          {/* Public Routes - Protected by Maintenance Guard */}
          <Route path="/*" element={
            <MaintenanceGuard>
              <PublicLayout>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/premium-peptides" element={<PeptidesShopPage />} />
                  <Route path="/premium-peptides/:legacySlug" element={<LegacyProductSlugRedirectPage />} />
                  <Route path="/premium-peptides/:categorySlug/:productSlug" element={<ProductDetailPage />} />
                  <Route path="/product/:productId" element={<LegacyProductRedirectPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/verify-email" element={<VerifyEmailPage />} />
                  <Route path="/peptides-research" element={<ResearchLibraryPage />} />
                  <Route path="/peptides-research/:slug" element={<ResearchArticlePage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms-conditions" element={<TermsConditionsPage />} />
                  <Route path="/accessibility" element={<AccessibilityPage />} />
                  <Route path="/compliance" element={<CompliancePage />} />
                  <Route path="/shipping-returns" element={<ShippingReturnsPage />} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/about" element={<AboutUsPage />} />
                  <Route path="/account" element={<UserPortal />} />
                  <Route path="/storage/*" element={<Navigate to="/" replace />} />
                  <Route path="/rv-repair/*" element={<Navigate to="/" replace />} />
                  <Route path="/employment/*" element={<Navigate to="/" replace />} />
                  <Route path="/job-application/*" element={<Navigate to="/" replace />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </PublicLayout>
            </MaintenanceGuard>
          } />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <SiteSettingsProvider>
          <AppContent />
        </SiteSettingsProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
